import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Chip,
  IconButton,
  Alert
} from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import { FaBitbucket, FaGitlab } from 'react-icons/fa';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import BuildIcon from '@mui/icons-material/Build';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { Key } from 'lucide-react';

const platforms = [
  { name: 'GitHub', icon: <GitHubIcon />, color: '#24292e' },
  { name: 'GitLab', icon: <FaGitlab />, color: '#fc6d26' },
  { name: 'Bitbucket', icon: <FaBitbucket />, color: '#0052cc' },
  { name: 'Azure', icon: <IntegrationInstructionsIcon />, color: '#0078d4' }
];

const getPlatformInstructions = (platform) => {
  const instructions = {
    GitHub: {
      title: 'GitHub',
      steps: [
        'Go to your repository on **GitHub**.',
        'Click **Settings** → **Webhooks** → **Add webhook**.',
        'Paste **Webhook URL** into the **Payload URL** field.',
        'Set **Content type** to **application/json**.',
        'Add **API Key** header: **X-API-KEY: [your key]**.',
        'Select **\'Let me select individual events\'** and check **\'Pull requests\'**.',
        'Click **\'Add webhook\'** to save.'
      ],
      alert: {
        type: 'warning',
        title: 'Important',
        message: 'Only pull request events are needed. Keep your API key secret.'
      }
    },
    GitLab: {
      title: 'GitLab',
      steps: [
        'Go to your repository → **Settings** → **Webhooks**.',
        'Paste **Webhook URL**.',
        'Add **API Key** header.',
        'Select trigger: **Merge Request events**.',
        'Save webhook.'
      ],
      alert: {
        type: 'warning',
        title: 'Important',
        message: 'Only merge request events are needed. Keep your API key secret.'
      }
    },
    Bitbucket: {
      title: 'Bitbucket',
      steps: [
        'Go to repository → **Settings** → **Webhooks** → **Add**.',
        'Paste **Webhook URL**.',
        'Set **Status** → **Active**.',
        'Triggers: **PR created + updated**.',
        'Add **API Key** header.',
        'Save webhook.'
      ],
      alert: {
        type: 'warning',
        title: 'Important',
        message: 'Only pull request events are needed. Keep your API key secret.'
      }
    },
    Azure: {
      title: 'Azure DevOps',
      steps: [
        'Go to **Project Settings** → **Service Connections** → **Webhooks**.',
        'Click **\'New Webhook\'**.',
        '**Name:** **\'AI Code Review\'**.',
        'Paste **Webhook URL**.',
        'Triggers: **PR created + updated**.',
        'Add **API Key** header.',
        'Save webhook.'
      ],
      alert: {
        type: 'warning',
        title: 'Important',
        message: 'Only pull request events are needed. Keep your API key secret.'
      }
    }
  };
  return instructions[platform] || instructions.GitHub;
};

const WebhookIntegrationStep = () => {
  const [selectedPlatform, setSelectedPlatform] = useState('GitHub');

  const currentInstructions = getPlatformInstructions(selectedPlatform);

  return (
    <>
      <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.25rem' }, lineHeight: { xs: '2rem', md: '2.5rem' } }}>Step 3: Integrate Webhooks</Typography>
      <Typography variant="body1" color="#6b7280" gutterBottom sx={{ mb: { xs: 2, md: 4 }, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }, lineHeight: { xs: '1.5rem', md: '1.75rem' } }}>
        Add the provided Webhook URL + API Key to your repo.
      </Typography>

      {/* Your Webhook Configuration Section */}
      <Box sx={{ mb: { xs: 2, md: 4 }, bgcolor: '#3c83f60d', border: '1px solid #3c83f633', p: { xs: 2, sm: 3, md: 6 }, borderRadius: '12px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, md: 3 } }}>
          <Box sx={{
            width: { xs: 20, sm: 24, md: 30 },
            height: { xs: 20, sm: 24, md: 30 },
            borderRadius: '50%',
            bgcolor: '#dbeafe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Key color='#3b82f6' size={18} />
          </Box>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
            Your Webhook Configuration
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                <Typography variant="h6" fontWeight={500} sx={{ color: '#1f1f1f', fontSize: { xs: '0.875rem', md: '1rem' } }}>{"Webhook URL:"}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <TextField
                  fullWidth
                  placeholder="https://api.example.com/webhooks/pr-analysis"
                  variant="outlined"
                  size="small"
                />
                <IconButton aria-label="Copy Webhook URL" size="small" sx={{ bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}>
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                <Typography variant="h6" fontWeight={500} sx={{ color: '#1f1f1f', fontSize: { xs: '0.875rem', md: '1rem' } }}>{"API Key:"}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <TextField
                  fullWidth
                  placeholder="••••••••••••••••"
                  variant="outlined"
                  size="small"
                />
                <IconButton aria-label="Copy API Key" size="small" sx={{ bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}>
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ 
          bgcolor: '#3c83f61a', 
          color: '#000', 
          border: 'none', 
          borderRadius: '12px', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'flex-start', 
          justifyContent: 'flex-start',
          p: { xs: 1.5, md: 2 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
              Location
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, lineHeight: 1.5 }}>
            You can find your unique Webhook URL and API Key in the Projects section of the app, under each project card.
          </Typography>
        </Alert>
      </Box>

      {/* Platform Integration Section */}
      <Box>
        <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f', mb: { xs: 2, md: 3 }, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
          Platform Integration
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 3, md: 6 }, mb: { xs: 2, md: 3 }, overflowX: { xs: 'auto', md: 'visible' } }}>
          {platforms.map((platform) => (
            <Chip
              key={platform.name}
              icon={platform.icon}
              label={platform.name}
              onClick={() => setSelectedPlatform(platform.name)}
              variant={selectedPlatform === platform.name ? 'filled' : 'outlined'}
              sx={{
                bgcolor: selectedPlatform === platform.name ? '#fff' : 'transparent',
                color: selectedPlatform === platform.name ? '#000' : '#6b7280',
                border: '0 solid #e2e8f0',
                borderRadius: '12px',
                px: { xs: 2, sm: 3, md: 4 },
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                height: { xs: 32, md: 40 },
                '& .MuiChip-icon': {
                  fontSize: { xs: '16px', md: '20px' }
                },
                flexShrink: 0,
                '&:hover': {
                  bgcolor: selectedPlatform === platform.name ? '#fff' : '#f3f4f6',
                }
              }}
            />
          ))}
        </Box>

        {/* Dynamic Platform Instructions */}
        <Box sx={{ bgcolor: 'white', border: '0 solid #e2e8f0', p: { xs: 2, sm: 3, md: 6 }, borderRadius: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
            {selectedPlatform === 'GitHub' && <GitHubIcon sx={{ color: '#24292e', fontSize: { xs: 20, md: 24 } }} />}
            {selectedPlatform === 'GitLab' && <Box sx={{ color: '#fc6d26', display: 'flex', alignItems: 'center', '& svg': { width: { xs: 20, md: 24 }, height: { xs: 20, md: 24 } } }}><FaGitlab /></Box>}
            {selectedPlatform === 'Bitbucket' && <Box sx={{ color: '#0052cc', display: 'flex', alignItems: 'center', '& svg': { width: { xs: 20, md: 24 }, height: { xs: 20, md: 24 } } }}><FaBitbucket /></Box>}
            {selectedPlatform === 'Azure' && <IntegrationInstructionsIcon sx={{ color: '#0078d4', fontSize: { xs: 20, md: 24 } }} />}
            <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
              {currentInstructions.title}
            </Typography>
          </Box>

          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            {currentInstructions.steps.map((step, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, md: 2 }, mb: { xs: 1, md: 1.5 } }}>
                <Box sx={{
                  width: { xs: 18, md: 20 },
                  height: { xs: 18, md: 20 },
                  borderRadius: '50%',
                  bgcolor: '#3b82f6',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: '9px', md: '10px' },
                  fontWeight: 600,
                  flexShrink: 0,
                  mt: 0.5
                }}>
                  {index + 1}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' } }}>
                  {step.split('**').map((part, i) =>
                    i % 2 === 1 ? (
                      <Typography key={i} component="span" variant="body2" fontWeight="bold" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' } }}>
                        {part}
                      </Typography>
                    ) : part
                  )}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Important Alert Box */}
          <Alert
            severity="warning"
            sx={{
              bgcolor: '#fef8e9',
              color: '#000',
              border: 'none',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              p: { xs: 1.5, md: 2 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                {currentInstructions.alert.title}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, lineHeight: 1.5 }}>
              {currentInstructions.alert.message}
            </Typography>
          </Alert>
        </Box>
      </Box>
    </>
  );
};

export default WebhookIntegrationStep;